// File: LoginScreen.js
//
// The public landing page (URL "/"). Shown to anyone who isn't signed
// in yet. Sign-in is handled by Google — when the user clicks
// "Continue with Google", we open the Google popup, then either send
// them to /home (existing user) or /sign-up (new user).
//
// Sections in this file:
//   - Icons + small reusable components (FeatureCard, ActionLink,
//     ContactButton, ContactRow). These keep the JSX below tidy.
//   - LoginScreen (the page itself): state, the Google sign-in
//     handler, and the rendered layout.
//
// What's likely to change:
//   - Hero headline and copy (look for the <h1> "Find resources..." )
//   - The three FeatureCards (Resources / Messages / Support)
//   - Helpful links section (Website, Facebook, Contact)
//   - The Contact popup at the bottom of the file (email/phone/fax)

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

import { auth, provider, signInWithPopup } from "./firebaseConfig";
import { getFirestore, doc, getDoc } from "firebase/firestore";

import logo from "./assets/512.png";

import websiteIcon from "./assets/icons/website.svg";
import facebookIcon from "./assets/icons/facebook.svg";
import contactIcon from "./assets/icons/contact.svg";

import { COLORS, THEME, pageBackground } from "./theme";

const db = getFirestore();

function IconMail({ className = "w-4 h-4" }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M4 6h16v12H4z" />
      <path d="M4 7l8 6 8-6" />
    </svg>
  );
}

function IconPhone({ className = "w-4 h-4" }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M22 16.9v3a2 2 0 0 1-2.2 2c-9.4-.9-16.8-8.3-17.7-17.7A2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.2 1.2.6 2.4 1.1 3.5a2 2 0 0 1-.5 2.2L8.6 10.5a16 16 0 0 0 4.9 4.9l1.1-1.1a2 2 0 0 1 2.2-.5c1.1.5 2.3.9 3.5 1.1a2 2 0 0 1 1.7 2z" />
    </svg>
  );
}

function IconFax({ className = "w-4 h-4" }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M6 9V4h12v5" />
      <path d="M6 18h12v2H6z" />
      <path d="M7 14h10" />
      <path d="M4 9h16a2 2 0 0 1 2 2v5h-4v-3H6v3H2v-5a2 2 0 0 1 2-2z" />
    </svg>
  );
}

// Small white card with a colored top stripe. Used three times on
// the hero card to show "Resources / Messages / Support". The
// `accent` prop is the color of the top stripe.
function FeatureCard({ title, text, accent }) {
  return (
    <div
      className="rounded-2xl border bg-white p-4 relative overflow-hidden"
      style={{
        borderColor: THEME.border,
        boxShadow: "0 10px 22px rgba(31,56,0,0.08)",
      }}
    >
      <div
        className="absolute inset-x-0 top-0 h-1"
        style={{ backgroundColor: accent }}
      />

      <div className="text-sm font-extrabold" style={{ color: THEME.text }}>
        {title}
      </div>

      <div className="text-xs mt-1" style={{ color: "rgba(31,56,0,0.62)" }}>
        {text}
      </div>
    </div>
  );
}

// A pill-shaped link that opens in a new tab. Used for the Website
// and Facebook buttons in the "Helpful links" row.
function ActionLink({ href, icon, label }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border bg-white hover:-translate-y-0.5 hover:shadow-md active:scale-[0.99] transition"
      style={{
        borderColor: THEME.border,
        boxShadow: "0 8px 18px rgba(31,56,0,0.06)",
      }}
    >
      <span
        className="w-9 h-9 rounded-xl flex items-center justify-center border"
        style={{
          backgroundColor: COLORS.purpleBase,
          borderColor: THEME.border,
        }}
      >
        <img src={icon} alt="" className="w-4 h-4" />
      </span>

      <span className="text-sm font-extrabold" style={{ color: THEME.text }}>
        {label}
      </span>
    </a>
  );
}

// The same pill style as ActionLink, but a button that triggers an
// action (used for "Contact", which opens the contact popup).
function ContactButton({ icon, label, onClick }) {
  return (
    <button
      onClick={onClick}
      className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border bg-white hover:-translate-y-0.5 hover:shadow-md active:scale-[0.99] transition"
      style={{
        borderColor: THEME.border,
        boxShadow: "0 8px 18px rgba(31,56,0,0.06)",
      }}
      type="button"
    >
      <span
        className="w-9 h-9 rounded-xl flex items-center justify-center border"
        style={{
          backgroundColor: COLORS.purpleBase,
          borderColor: THEME.border,
        }}
      >
        <img src={icon} alt="" className="w-4 h-4" />
      </span>

      <span className="text-sm font-extrabold" style={{ color: THEME.text }}>
        {label}
      </span>
    </button>
  );
}

// One row inside the Contact popup (icon + label + value, e.g. an
// email or phone number). Clicking the row opens the relevant link
// (mailto:, tel:, etc.).
function ContactRow({ href, icon, label, value }) {
  return (
    <a
      href={href}
      className="rounded-2xl border bg-white px-4 py-3 hover:bg-[#E7F7D2] transition flex items-center gap-3"
      style={{
        borderColor: THEME.border,
        boxShadow: "0 8px 18px rgba(31,56,0,0.06)",
      }}
    >
      <span
        className="w-10 h-10 rounded-xl flex items-center justify-center border shrink-0"
        style={{
          color: THEME.primary,
          backgroundColor: COLORS.greenLight,
          borderColor: THEME.border,
        }}
      >
        {icon}
      </span>

      <span
        className="text-sm font-extrabold shrink-0"
        style={{ color: THEME.text }}
      >
        {label}
      </span>

      <span
        className="text-sm ml-auto text-right break-all"
        style={{ color: "rgba(31,56,0,0.68)" }}
      >
        {value}
      </span>
    </a>
  );
}

function LoginScreen() {
  const navigate = useNavigate();

  // Status flags + the Contact popup visibility.
  const [loginInProgress, setLoginInProgress] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const [showContact, setShowContact] = useState(false);

  // Runs when the "Continue with Google" button is clicked. Pops up
  // Google's sign-in window, then routes the user based on whether
  // they already have a profile in our database.
  const handleContinueWithGoogle = async () => {
    try {
      setLoginInProgress(true);
      setStatusMessage("");

      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      const userRef = doc(db, "users", user.uid);
      const snap = await getDoc(userRef);

      if (snap.exists()) navigate("/home");
      else navigate("/sign-up");
    } catch (err) {
      console.error(err);

      const code = err?.code || "";

      if (code === "auth/popup-blocked") {
        setStatusMessage("Popup was blocked. Please allow popups and try again.");
      } else if (
        code === "auth/cancelled-popup-request" ||
        code === "auth/popup-closed-by-user"
      ) {
        setStatusMessage("Sign-in cancelled.");
      } else {
        setStatusMessage("Google sign-in failed. Please try again.");
      }
    } finally {
      setLoginInProgress(false);
    }
  };

  return (
    <div className="flex-1 relative flex flex-col" style={pageBackground}>
      {/* --- Page header: logo on left, Contact button on right --- */}
      <header
        className="sticky top-0 z-20 backdrop-blur border-b shadow-[0_6px_18px_rgba(31,56,0,0.16)]"
        style={{
          backgroundColor: THEME.headerBg,
          borderColor: "rgba(231,247,210,0.28)",
        }}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-2 sm:py-3 flex items-center justify-between gap-3">
          <button
            onClick={handleContinueWithGoogle}
            disabled={loginInProgress}
            className="flex items-center gap-3 rounded-xl px-2 py-1 hover:bg-white/10 transition"
            aria-label="Go to landing page"
            type="button"
          >
            <img src={logo} alt="Logo" className="w-13 h-12 object-contain" />

            <div className="leading-tight text-left">
              <div className="text-sm font-extrabold text-white">
                H.O.P.E Navigator
              </div>
              <div
                className="text-[12px] font-semibold"
                style={{ color: COLORS.yellowLight }}
              >
                Sign in
              </div>
            </div>
          </button>

          <button
            onClick={() => setShowContact(true)}
            className="text-sm font-extrabold text-white/90 hover:text-white transition rounded-xl px-3 py-2 hover:bg-white/10"
            type="button"
          >
            Contact
          </button>
        </div>
      </header>

      {/* --- Main two-column layout. Left = welcome card with feature
              tiles and helpful links. Right = the actual sign-in card.
              On mobile they stack vertically. --- */}
      <main className="flex-1 max-w-6xl mx-auto px-4 sm:px-6 py-4 sm:py-6 lg:py-8 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6 items-start">
          <section
            className="backdrop-blur border rounded-3xl p-5 sm:p-6 lg:p-8"
            style={{
              backgroundColor: THEME.cardBg,
              borderColor: THEME.border,
              boxShadow: THEME.shadow,
            }}
          >
            <div
              className="inline-flex items-center rounded-full px-3 py-1 text-sm font-extrabold"
              style={{
                color: COLORS.brown,
              }}
            >
              Welcome
            </div>

            <h1
              className="text-3xl sm:text-4xl font-extrabold mt-4 leading-tight"
              style={{ color: THEME.text }}
            >
              Find resources and message our team.
            </h1>

            <p
              className="mt-3 max-w-2xl text-base leading-7"
              style={{ color: "rgba(31,56,0,0.68)" }}
            >
              H.O.P.E Navigator helps you access community resources, ask questions,
              and communicate with Increasing H.O.P.E so the team can help you
              plan your next steps.
            </p>

            <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
              <FeatureCard
                title="Resources"
                text="Find local organizations and helpful links."
                accent={COLORS.greenBase}
              />

              <FeatureCard
                title="Messages"
                text="Ask questions and follow up with staff."
                accent={COLORS.yellowBase}
              />

              <FeatureCard
                title="Support"
                text="Get guidance as you move forward."
                accent={COLORS.purpleBase}
              />
            </div>

            <div
              className="mt-6 rounded-2xl border p-4"
              style={{
                backgroundColor: THEME.cardBgSoft,
                borderColor: THEME.border,
              }}
            >
              <div
                className="text-sm font-extrabold"
                style={{ color: THEME.text }}
              >
                Helpful links
              </div>

              <div className="mt-3 flex flex-wrap gap-3">
                <ActionLink
                  href="https://increasinghope.org/"
                  icon={websiteIcon}
                  label="Website"
                />

                <ActionLink
                  href="https://www.facebook.com/increasinghope"
                  icon={facebookIcon}
                  label="Facebook"
                />

                <ContactButton
                  icon={contactIcon}
                  label="Contact"
                  onClick={() => setShowContact(true)}
                />
              </div>
            </div>
          </section>

          <aside className="lg:sticky lg:top-[72px]">
            <div
              className="rounded-2xl border p-4 sm:p-5 lg:p-6 backdrop-blur relative overflow-hidden"
              style={{
                backgroundColor: THEME.cardBg,
                borderColor: THEME.border,
                boxShadow: "0 14px 30px rgba(31,56,0,0.1)",
              }}
            >
              <div
                className="absolute inset-x-0 top-0 h-1"
                style={{ backgroundColor: THEME.primary }}
              />

              <div
                className="text-sm font-extrabold uppercase tracking-wide"
                style={{ color: COLORS.purpleDark }}
              >
                Account access
              </div>

              <div
                className="text-2xl font-extrabold mt-2"
                style={{ color: THEME.text }}
              >
                Sign in
              </div>

              <div
                className="text-sm mt-2 leading-6"
                style={{ color: "rgba(31,56,0,0.66)" }}
              >
                Continue with Google to access your account, inquiries, resources,
                and message threads.
              </div>

              <button
                onClick={handleContinueWithGoogle}
                disabled={loginInProgress}
                className="mt-5 w-full rounded-xl px-4 py-3 text-base font-extrabold text-white transition hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none focus:ring-4"
                style={{
                  backgroundColor: COLORS.greenBase,
                  boxShadow: "0 10px 20px rgba(64,115,0,0.18)",
                }}
                type="button"
              >
                {loginInProgress ? "Signing in..." : "Continue with Google"}
              </button>

              {statusMessage && (
                <div className="mt-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
                  {statusMessage}
                </div>
              )}

              <div
                className="mt-5 text-xs leading-relaxed rounded-2xl border px-4 py-3"
                style={{
                  color: "rgba(31,56,0,0.62)",
                  backgroundColor: COLORS.greenLight,
                  borderColor: THEME.border,
                }}
              >
                If the sign-in window does not open, allow popups for this site
                and try again.
              </div>
            </div>

          </aside>
        </div>
      </main>

      {/* --- Contact popup. Hidden until the user clicks "Contact" in
              the header or in the helpful-links row. The phone/email/fax
              values to display live inside this block. --- */}
      {showContact && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/55 px-5">
          <div
            className="w-full max-w-md rounded-3xl bg-white shadow-2xl border overflow-hidden"
            style={{ borderColor: THEME.border }}
          >
            <div
              className="px-5 py-4 border-b flex items-center justify-between"
              style={{
                borderColor: THEME.border,
                backgroundColor: COLORS.greenLight,
              }}
            >
              <div>
                <div
                  className="font-extrabold"
                  style={{ color: THEME.text }}
                >
                  Contact
                </div>

                <div
                  className="text-sm mt-1"
                  style={{ color: "rgba(31,56,0,0.64)" }}
                >
                  Reach Increasing H.O.P.E
                </div>
              </div>

              <button
                onClick={() => setShowContact(false)}
                className="w-9 h-9 rounded-xl bg-white hover:bg-black/5 transition flex items-center justify-center border"
                style={{
                  color: THEME.text,
                  borderColor: THEME.border,
                }}
                type="button"
                aria-label="Close"
              >
                <span className="text-lg leading-none">×</span>
              </button>
            </div>

            <div className="p-5">
              <div className="flex flex-col gap-3">
                <ContactRow
                  href="mailto:financetraining@increasinghope.org"
                  icon={<IconMail />}
                  label="Email"
                  value="financetraining@increasinghope.org"
                />

                <ContactRow
                  href="tel:8432254343"
                  icon={<IconPhone />}
                  label="Phone"
                  value="(843) 225-4343"
                />

                <ContactRow
                  href="tel:8665796265"
                  icon={<IconFax />}
                  label="Fax"
                  value="866-579-6265"
                />
              </div>

              <div className="mt-5 text-right">
                <button
                  onClick={() => setShowContact(false)}
                  className="px-4 py-2 rounded-xl text-sm font-extrabold transition hover:opacity-90"
                  style={{
                    color: COLORS.brown,
                    backgroundColor: COLORS.yellowBase,
                    boxShadow: "0 10px 20px rgba(61,51,0,0.14)",
                  }}
                  type="button"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default LoginScreen;